/**
 * How tall a grid draws its rows.
 *
 * Three heights, because that is what the backend stores -- an enum, not a
 * number -- and the three are what the row window is planned against: a taller
 * row means fewer of them on screen and a smaller buffer, which the store works
 * out for itself the moment the container reports its new geometry.
 */
import { Rows3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { ViewOf } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { ROW_HEIGHTS, type RowHeightSize } from '../grid/columns'
import { useGridSettings } from './grid-settings-mutations'

const SIZES = Object.keys(ROW_HEIGHTS) as RowHeightSize[]

export function RowHeightMenu({
  tableId,
  view,
}: {
  tableId: number
  view: ViewOf<'grid'>
}) {
  const { t } = useTranslation()
  const { setRowHeight } = useGridSettings(tableId, view.id)
  const chosen: RowHeightSize = view.row_height_size ?? 'small'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Rows3 className="size-4 opacity-70" />
          {t('view.rowHeight')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SIZES.map((size) => (
          <DropdownMenuCheckboxItem
            key={size}
            checked={size === chosen}
            onSelect={() => {
              setRowHeight(size)
            }}
          >
            {t(`view.rowHeight_${size}`)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
